<template>
    <div>
        <div ref="chartWrapper" class="w-100 h-100">
            <b-overlay :show="showSpinner" rounded="sm" spinner-variant="primary">
                <ejs-chart :palettes="palettes" :id="'chart_'+dashboardWidgetConfigurationId"
                           :primaryXAxis="primaryXAxis"
                           :primaryYAxis="primaryYAxis"
                           :legendSettings="legendSettings"
                           :font="font"
                           :tooltip="tooltip"
                           :height="chartHeight"
                           :width="chartWidth"
                           :tooltipRender='tooltipRender'
                           v-if="chartDataLoaded">
                    <e-series-collection>
                        <e-series v-for="(series,index) in chartData" :key="index" :dataSource='series.seriesData' type='StackingColumn' xName='label' yName='value' :name='series.seriesLabel'> </e-series>
                    </e-series-collection>
                </ejs-chart>
            </b-overlay>
        </div>

        <b-modal v-model="modalVisible" title="Settings" @ok="handleModalOk">
            <b-form-group label="Title" label-for="widgetTitle">
                <b-form-input name="widgetTitle" v-model="widgetTitle"></b-form-input>
            </b-form-group>
            <b-form-group label="Court/Tribunal/Other"
                          label-for="courtRegistryId">
                <select2 name="courtRegistryId"
                         :options="allCourtRegistries"
                         multiple
                         key-property="courtRegistryId"
                         text-property="name"
                         v-model="currentConfiguration.courtRegistryIds">
                </select2>
            </b-form-group>
            <b-form-group label="Organisation"
                          label-for="interviewOrganisatoinId">
                <select2 name="interviewOrganisatoinId"
                         :options="allInterviewOrganisations"
                         multiple
                         key-property="interviewOrganisationId"
                         text-property="name"
                         v-model="currentConfiguration.interviewOrganisationIds">
                </select2>
            </b-form-group>
        </b-modal>
    </div>
</template>

<script>
    import Vue from "vue";
    import { StackingColumnSeries, ColumnSeries, Legend, Category, Tooltip, Highlight } from "@syncfusion/ej2-vue-charts";
    import { ButtonPlugin } from '@syncfusion/ej2-vue-buttons';
    import { BootstrapVueIcons } from 'bootstrap-vue';
    import axios from 'axios';
    import ErrorsMixin from '../../../mixins/ErrorsMixin';
    import NumbersMixin from '../../../mixins/NumbersMixin';

    Vue.use(ButtonPlugin);
    Vue.use(BootstrapVueIcons);

    export default {
        name: 'CostingAveragesWidget',
        mixins: [ErrorsMixin, NumbersMixin ],
        props: {
            dashboardWidgetConfigurationId: {
                type: String,
                default: null
            },
            title: {
                type: String,
                default: null
            },
            configuration: {
                type: Object,
                default: null
            },
            widgetMode: {
                type: String,
                default: 'view'
            },
            selectedTimeRange: {
                type: Number,
                required: true
            }
        },

        data() {
            return {
    
                showSpinner: false,
                // 3 colors for the stacked bars
                palettes: ["#E94649", "#6FAAB0", "#C4C24A"],
                currentConfiguration: this.configuration ? this.configuration : { courtRegistryIds: null, interviewOrganisationIds: null },
                allCourtRegistries: null,
                allInterviewOrganisations: null,
                modalVisible: false,
                widgetTitle: this.title,
                chartData: [],
                tooltip: {
                    enable: true
                    // for formatting and displaying correct currency please see method :tooltipRender
                },

                font: { fontFamily: 'Segoe UI', fontWeight: '400', size: '12px', color: "#ffffff" },

                primaryXAxis: {
                    valueType: 'Category',
                    title: 'Time',
                    titleStyle: {
                        size: '14px', color: '#444',
                        fontFamily: 'Segoe UI', fontWeight: ''
                    },
                    labelStyle: {
                        size: '12px', color: '#888',
                        fontFamily: 'Segoe UI', fontWeight: ''
                    }
                },

                primaryYAxis:
                {
                    title: this.title,
                    lineStyle: { width: 0 },
                    majorTickLines: { width: 0 },
                    majorGridLines: { width: 1 },
                    minorGridLines: { width: 1 },
                    minorTickLines: { width: 0 },
                    titleStyle: {
                        size: '14px', color: '#444',
                        fontFamily: 'Segoe UI', fontWeight: ''
                    },
                    labelStyle: {
                        size: '12px', color: '#888',
                        fontFamily: 'Segoe UI', fontWeight: ''
                    }
                },

                animation: { enable: false },
                legendSettings: {
                    visible: true,
                    position: 'Top'
                },

                chartDataLoaded: false,
                // takes care of properly resizing/rendering chart when dragged/resized
                resizeObserver: null,
                chartHeight: '',
                chartWidth: '',
            };
        },

        provide: {
            chart: [StackingColumnSeries, ColumnSeries, Legend, Category, Tooltip, Highlight]
        },
        watch: {
            selectedTimeRange: function () {
                this.reloadData();
            },
        },

        methods: {

            tooltipRender: function (args) {
                args.text = args.point.x + '' + ': ' + this.formatAsCurrency(args.point.y);
            },
            handleModalOk() {
                this.modalVisible = false;
                this.$emit('configurationChanged', this.widgetTitle, this.currentConfiguration);
            },

            loadData: function () {

                if (this.dashboardWidgetConfigurationId) {

                     var url = this.$urlHelper.getApiUrl('dashboardwidget', 'data', ['costingaverages', this.dashboardWidgetConfigurationId, this.selectedTimeRange]);
                    //var url = this.$urlHelper.getApiUrl('dashboardwidget', 'data', ['casecategoryaverages', this.dashboardWidgetConfigurationId, this.selectedTimeRange]);

                    this.showSpinner = true;
                    axios
                        .get(url)
                        .then(response => {

                            if (this.selectedTimeRange == 2) this.primaryXAxis.title = 'Day';
                            if (this.selectedTimeRange == 3) this.primaryXAxis.title = 'Month';
                            if (this.selectedTimeRange == 4) this.primaryXAxis.title = 'Year';

                            this.chartData = response.data;
                            this.chartDataLoaded = false;
                            this.showSpinner = false;

                            this.$nextTick(() => {
                                this.chartDataLoaded = true;
                            });
                        })
                        .catch(ex => {
                            this.showSpinner = false;
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },

            reloadData: function () {
                if (this.widgetMode == 'view') {
                    this.loadData();
                }
            },

            editConfiguration: function () {
                if ((this.allCourtRegistries == null) && (this.allInterviewOrganisations == null)) {
                    var promises = [];
                    var url = this.$urlHelper.getApiUrl('courtregistry', 'all');
                    promises.push(
                        axios
                            .get(url)
                            .then(response => {
                                this.allCourtRegistries = response.data;
                            }));
                    url = this.$urlHelper.getApiUrl('intervieworganisation', 'all');
                    promises.push(
                        axios
                            .get(url)
                            .then(response => {
                                this.allInterviewOrganisations = response.data;
                            }));
                    Promise
                        .all(promises)
                        .then(() => {
                            this.modalVisible = true;
                        });
                } else {
                    this.modalVisible = true;
                }
            },

            onResize: function () {
                var chartWrapperSize = this.$refs.chartWrapper.getBoundingClientRect();
                this.chartWidth = Math.trunc(chartWrapperSize.width) + "px";
                this.chartHeight = Math.trunc(chartWrapperSize.height) + "px";
            }
        },

        mounted: function () {
            this.resizeObserver = new ResizeObserver(this.onResize);
            this.resizeObserver.observe(this.$refs.chartWrapper);
            this.loadData();
        },

        beforeDestroy() {
            this.resizeObserver.unobserve(this.$refs.chartWrapper);
        }
    }
</script>